import { offset } from '@floating-ui/dom'
import {
  FloatingArrow,
  FloatingContext,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  OffsetOptions,
  UseFloatingOptions,
  useFloatingNodeId,
  useTransitionStyles,
} from '@floating-ui/react'
import { default as React } from 'react'
import { IPopover } from '../Popover/hooks/usePopper'
import { IHoverPopover, useHoverPopper } from './hooks/useHoverPopper'

interface IProps
  extends Omit<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>,
    'title'
  > {
  title: string | React.ReactElement
  placement?: UseFloatingOptions['placement'] | 'auto'
  popupProps?: React.HTMLAttributes<HTMLDivElement>
  popperOptions?: Partial<UseFloatingOptions>
  offsetOptions?: OffsetOptions
  isShownOnHover?: boolean
}

export const Tooltip = ({
  title,
  children,
  popupProps,
  popperOptions,
  offsetOptions,
  placement = 'auto',
  ...props
}: React.PropsWithChildren<IProps>) => {
  const { target, toggle, targetProps, ...tooltipProps } = useHoverPopper<HTMLButtonElement>({
    placement,
    middleware: offsetOptions ? [offset(offsetOptions)] : undefined,
  })
  if (!title) {
    return <>{children}</>
  }
  const className: string | undefined = ['tooltip-target', props.className, targetProps.className]
    .filter(Boolean)
    .join(' ')
    .trim()
  return (
    <>
      <span ref={target} {...props} {...targetProps} className={className}>
        {children}
      </span>
      <TooltipPopover {...{ ...tooltipProps, ...popupProps }}>{title}</TooltipPopover>
    </>
  )
}

interface PopoverValue<T extends HTMLElement>
  extends Omit<IPopover<T>, 'target' | 'targetProps' | 'toggle' | 'context'>,
    Partial<Pick<IHoverPopover<T>, 'isInstantPhase' | 'delayId'>> {
  context: FloatingContext<any>
}

export interface ITooltipPopupProps<T extends HTMLElement = any> extends PopoverValue<T> {}

export const TooltipPopover = <T extends HTMLElement>({
  context,
  isOpen,
  children,
  refs,
  floatingStyles,
  getFloatingProps,
  arrowRef,
  isInstantPhase,
  delayId,
}: React.PropsWithChildren<ITooltipPopupProps<T>>) => {
  const nodeId = useFloatingNodeId()
  const instantDuration = 0
  const duration = 250
  const { isMounted, styles } = useTransitionStyles(context, {
    duration:
      isInstantPhase && isOpen
        ? {
            open: instantDuration,
            close: delayId === nodeId ? duration : instantDuration,
          }
        : duration,
    initial: {
      opacity: 0,
    },
  })
  if (!isMounted) return null
  return (
    <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
      <FloatingNode id={nodeId}>
        <FloatingPortal>
          <div
            className="tooltip"
            ref={refs.setFloating}
            style={{ maxWidth: 250, ...floatingStyles, ...styles }}
            {...getFloatingProps()}
          >
            {children}
            <FloatingArrow ref={arrowRef} context={context} />
          </div>
        </FloatingPortal>
      </FloatingNode>
    </FloatingFocusManager>
  )
}
